import type { ProjectCurrency } from '../utils/currency';
import { apiGet, apiPut, planningPath } from './PlanningApiClient';

/**
 * The four choices a project must make for itself. Nothing is defaulted: until
 * a person chooses, each is an empty string and is listed in `missing`.
 * `currency` is read-only once the project holds money (`currencyLocked`).
 */
export type ProjectChoiceKey = 'currency' | 'timezone' | 'locale' | 'phone_region';

export interface ProjectChoices {
  currency: ProjectCurrency;
  /** Language tag used to format numbers and dates, e.g. en-GB. */
  locale: string;
  /** IANA timezone name, e.g. Europe/London. */
  timezone: string;
  /** Two-letter country code used to read phone numbers, e.g. GB. */
  phoneRegion: string;
  currencyLocked: boolean;
  missing: ProjectChoiceKey[];
}

const CHOICE_KEYS: ProjectChoiceKey[] = ['currency', 'timezone', 'locale', 'phone_region'];

export const NO_PROJECT_CHOICES: ProjectChoices = {
  currency: '',
  locale: '',
  timezone: '',
  phoneRegion: '',
  currencyLocked: false,
  missing: [...CHOICE_KEYS],
};

function textOrEmpty(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** Reads the four choices out of GET /planning/:project_id/settings. */
export function parseProjectChoices(view: Record<string, unknown> | null | undefined): ProjectChoices {
  const v = view ?? {};
  const choices = {
    currency: textOrEmpty(v.currency).toUpperCase() as ProjectCurrency,
    locale: textOrEmpty(v.locale),
    timezone: textOrEmpty(v.timezone),
    phoneRegion: textOrEmpty(v.phone_region).toUpperCase(),
  };
  const asKey: Record<ProjectChoiceKey, string> = {
    currency: choices.currency, timezone: choices.timezone, locale: choices.locale, phone_region: choices.phoneRegion,
  };
  // Trust the API's own list; work it out ourselves only when it is absent.
  const missing = Array.isArray(v.missing)
    ? (v.missing.filter((k): k is ProjectChoiceKey => CHOICE_KEYS.includes(k as ProjectChoiceKey)))
    : CHOICE_KEYS.filter(k => asKey[k] === '');
  return { ...choices, currencyLocked: v.currency_locked === true, missing };
}

/**
 * Settings stored in the API (the project's planning data). Currency, timezone, language and phone country are NOT here: see ProjectChoices.
 * The API names this section `project` and still sends the older `wedding` name next to it. The inner keys
 * `weddingWebsite` and `weddingUpdate` are saved data and keep their spelling.
 */
export interface DbSettings {
  id?: string;
  /** The project's four choices, read from the same response. */
  choices: ProjectChoices;
  project: {
    coupleNames: string;
    brideName: string;
    groomName: string;
    hashtag: string;
    rsvpDeadline: string;
    guestCapacity: number;
    paymentMethod?: {
      bankName: string;
      accountNumber: string;
      accountName: string;
      mobileMoney?: string;
      reference?: string;
    };
    weddingWebsite: {
      enabled: boolean;
      url: string;
      passwordProtected: boolean;
      password?: string;
    };
  };
  guests: {
    defaultRSVPStatus: 'pending' | 'confirmed' | 'declined';
    allowPlusOnes: boolean;
    maxPlusOnes: number;
    requirePhone: boolean;
    requireEmail: boolean;
    guestCategories: string[];
    seatingChart: {
      enabled: boolean;
      autoAssign: boolean;
    };
    communications: {
      welcomeMessage: string;
      reminderDays: number[];
      followUpDays: number[];
    };
  };
  vendors: {
    defaultCategories: string[];
    paymentTerms: {
      depositPercentage: number;
      finalPaymentDays: number;
    };
    contractTemplates: string[];
    vendorRatings: {
      enabled: boolean;
      criteria: string[];
    };
    budgetAlerts: {
      enabled: boolean;
      threshold: number;
    };
  };
  communications: {
    emailProvider: 'gmail' | 'outlook' | 'custom';
    smtpSettings?: {
      host: string;
      port: number;
      username: string;
      password: string;
    };
    smsProvider: 'twilio' | 'africastalking' | 'custom';
    smsSettings?: {
      apiKey: string;
      senderId: string;
    };
    whatsapp: {
      enabled: boolean;
      templates: string[];
      // Channel invite link the planner shares with RSVP-confirmed guests
      // (#371). Stored in comms_config.whatsapp.channelLink on ans-backend —
      // same nested-JSON pattern, no new settings shape.
      channelLink?: string;
    };
    templates: {
      rsvpConfirmation: string;
      paymentReminder: string;
      weddingUpdate: string;
      thankYou: string;
    };
  };
}

export const DEFAULT_DB_SETTINGS: DbSettings = {
  choices: NO_PROJECT_CHOICES,
  project: {
    coupleNames: '',
    brideName: '',
    groomName: '',
    hashtag: '',
    rsvpDeadline: '',
    guestCapacity: 0,
    weddingWebsite: {
      enabled: false,
      url: '',
      passwordProtected: false,
    },
  },
  guests: {
    defaultRSVPStatus: 'pending',
    allowPlusOnes: true,
    maxPlusOnes: 2,
    requirePhone: true,
    requireEmail: true,
    guestCategories: [],
    seatingChart: {
      enabled: false,
      autoAssign: false,
    },
    communications: {
      welcomeMessage: '',
      reminderDays: [7, 3, 1],
      followUpDays: [1, 7],
    },
  },
  vendors: {
    defaultCategories: [],
    paymentTerms: {
      depositPercentage: 50,
      finalPaymentDays: 7,
    },
    contractTemplates: [],
    vendorRatings: {
      enabled: true,
      criteria: ['quality', 'timeliness', 'communication'],
    },
    budgetAlerts: {
      enabled: true,
      threshold: 80,
    },
  },
  communications: {
    emailProvider: 'gmail',
    smsProvider: 'twilio',
    whatsapp: {
      enabled: true,
      templates: [],
      channelLink: '',
    },
    templates: {
      rsvpConfirmation: '',
      paymentReminder: '',
      weddingUpdate: '',
      thankYou: '',
    },
  },
};

type Json = Record<string, unknown>;

function isPlainObject(v: unknown): v is Json {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** Same pattern the API uses to recognise a credential by its key name. */
const SECRET_KEY = /(secret|token|pass(word|wd)?\b|api[_-]?key|private[_-]?key|credential|authorization)/i;

/**
 * Removes credential-looking keys at any depth. The API refuses a settings save
 * that carries a credential (they belong in encrypted provider keys, not in
 * settings), so nothing secret is ever sent from here.
 */
export function stripSecretKeys<T>(value: T): T {
  if (Array.isArray(value)) return value.map(v => stripSecretKeys(v)) as unknown as T;
  if (!isPlainObject(value)) return value;
  const out: Json = {};
  for (const [k, v] of Object.entries(value)) {
    if (SECRET_KEY.test(k)) continue;
    out[k] = stripSecretKeys(v);
  }
  return out as unknown as T;
}

/** The API returns null for a block nobody has saved yet; fall back to the form defaults for it. */
function block<T extends object>(defaults: T, saved: unknown): T {
  return isPlainObject(saved) ? { ...defaults, ...(saved as Partial<T>) } : { ...defaults };
}

/** Maps the API's settings view onto the shape the Settings form edits. */
function toDbSettings(view: Json): DbSettings {
  // `project` is the current name; `wedding` is what a server that has not been updated sends.
  const projectConfig = (isPlainObject(view.project) ? view.project : isPlainObject(view.wedding) ? view.wedding : {}) as Json;
  // Older saves kept the currency inside this block; the API now owns it.
  const { defaultBudgetCurrency: _c, primaryCurrencyLocked: _l, ...savedProject } = projectConfig;
  const capacity = isPlainObject(view.capacity) ? view.capacity : null;
  const maxGuests = capacity && typeof capacity.max_guests === 'number' ? capacity.max_guests : null;
  const project = block(DEFAULT_DB_SETTINGS.project, savedProject);
  return {
    choices: parseProjectChoices(view),
    project: { ...project, guestCapacity: maxGuests ?? project.guestCapacity ?? 0 },
    guests: block(DEFAULT_DB_SETTINGS.guests, view.guests),
    vendors: block(DEFAULT_DB_SETTINGS.vendors, view.vendors),
    communications: block(DEFAULT_DB_SETTINGS.communications, view.communications),
  };
}

/**
 * Loads the project's settings. A failure is thrown, never replaced by defaults
 * or a stored copy: saving from a form full of defaults would overwrite the
 * real settings, and a stored copy could belong to another project.
 */
export async function loadSettingsFromDb(): Promise<DbSettings> {
  const view = await apiGet<Json>(planningPath('/settings'));
  return toDbSettings(view ?? {});
}

/**
 * Saves the form's blocks. Currency, timezone, language and phone country are
 * not sent here (see saveProjectChoices), and no credential ever is.
 */
export async function saveSettingsToDb(settings: Omit<DbSettings, 'choices'> & { choices?: ProjectChoices }): Promise<DbSettings> {
  const { defaultBudgetCurrency: _c, primaryCurrencyLocked: _l, ...project } = settings.project as DbSettings['project'] & Json;
  const view = await apiPut<Json>(planningPath('/settings'), {
    project,
    // For this one release the same block is also sent under the old name, so an API
    // rollback or a stale tab on the old name cannot silently drop a save. The server
    // treats identical keys as one. Remove with alfa2267/ans-main#576.
    wedding: project,
    guests: settings.guests,
    vendors: settings.vendors,
    communications: stripSecretKeys(settings.communications),
  });
  return toDbSettings(view ?? {});
}

/** The project's four choices (currency, language, timezone, phone country). */
export async function loadProjectChoices(): Promise<ProjectChoices> {
  const view = await apiGet<Json>(planningPath('/settings'));
  return parseProjectChoices(view);
}

export interface ProjectChoicesChange {
  currency?: ProjectCurrency;
  locale?: string;
  timezone?: string;
  phoneRegion?: string;
}

/**
 * Saves only the choices that changed. The API refuses a currency change with a
 * 409 once the project holds money; the caller shows its plain message.
 */
export async function saveProjectChoices(change: ProjectChoicesChange): Promise<ProjectChoices> {
  const body: Json = {};
  if (change.currency !== undefined) body.currency = change.currency;
  if (change.locale !== undefined) body.locale = change.locale;
  if (change.timezone !== undefined) body.timezone = change.timezone;
  if (change.phoneRegion !== undefined) body.phone_region = change.phoneRegion;
  const view = await apiPut<Json>(planningPath('/settings'), body);
  return parseProjectChoices(view);
}

/**
 * Reads the couple's configured WhatsApp channel invite link (#371 —
 * "Auto-add RSVP-confirmed guests to the couple's WhatsApp channel").
 * A thin call against ans-backend's dedicated endpoint rather than loading and
 * unwrapping the whole settings blob, since this is read from the guest
 * list/detail "Add to WhatsApp channel" action, not the Settings page.
 */
export async function getWhatsAppChannelLink(): Promise<string | null> {
  const res = await apiGet<{ channelLink: string | null }>(
    planningPath('/whatsapp-channel-link')
  );
  return res.channelLink ?? null;
}

/** Owner-only: upserts the WhatsApp channel invite link from the Settings page. */
export async function saveWhatsAppChannelLink(channelLink: string): Promise<string> {
  const res = await apiPut<{ channelLink: string }>(
    planningPath('/whatsapp-channel-link'),
    { channelLink }
  );
  return res.channelLink;
}

